import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { AiOutlineFullscreen, AiOutlineFullscreenExit } from 'react-icons/ai';
import { useComputed } from '@preact/signals-react';
import { useDebouncedCallback } from 'use-debounce';
import { MonacoEditor } from '@/features/editor/widgets/code-editors/monaco-editor';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { CompoundChange } from '@/pkg/sdk';

export function AiComponentCode() {
  const selectedTab = useAiComponentContext((ctx) => ctx.selectedCodeTab);
  const newChange = useAiComponentContext((ctx) => ctx.newChange);
  const setHtml = useAiComponentContext((ctx) => ctx.setHtml);
  const setCss = useAiComponentContext((ctx) => ctx.setCss);
  const setJs = useAiComponentContext((ctx) => ctx.setJs);
  const setEditManifest = useAiComponentContext((ctx) => ctx.setEditManifest);
  const isFullscreen = useAiComponentContext((ctx) => ctx.isFullScreen);
  const toggleFullscreen = useAiComponentContext((ctx) => ctx.toggleFullScreen);

  const html = useComputed(
    () => (newChange?.value?.block?.value as CompoundChange)?.html || ``,
  );
  const css = useComputed(
    () => (newChange?.value?.block?.value as CompoundChange)?.css || ``,
  );
  const js = useComputed(
    () => (newChange?.value?.block?.value as CompoundChange)?.js || ``,
  );

  const currentCode = useComputed(() => {
    if (selectedTab.value === `HTML`) {
      return html.value;
    }
    if (selectedTab.value === `CSS`) {
      return css.value;
    }
    if (selectedTab.value === `JS`) {
      return js.value;
    }
    if (selectedTab.value === `M`) {
      return JSON.stringify(
        (newChange?.value?.block?.value as CompoundChange)?.editManifest,
        null,
        2,
      );
    }
    return ``;
  });

  const currentLang = useComputed(() => {
    if (selectedTab.value === `HTML`) {
      return `html`;
    }
    if (selectedTab.value === `CSS`) {
      return `css`;
    }
    if (selectedTab.value === `JS`) {
      return `javascript`;
    }
    if (selectedTab.value === `M`) {
      return `json`;
    }
    return `html`;
  });

  // Use a ref to track the current tab when code changes are made
  const currentTabRef = useRef(selectedTab.value);

  // Update the ref whenever the tab changes
  useEffect(() => {
    currentTabRef.current = selectedTab.value;
  }, [selectedTab.value]);

  // Debounced callback that uses the ref to ensure correct setter is called
  const onCodeChanged = useDebouncedCallback(
    (code: string, tabAtTimeOfEdit: string) => {
      // Only apply the change if we're still on the same tab that initiated the edit
      if (currentTabRef.current !== tabAtTimeOfEdit) {
        // Silently discard the edit - this is expected behavior when switching tabs quickly
        return;
      }

      if (tabAtTimeOfEdit === `HTML`) {
        setHtml(code);
      } else if (tabAtTimeOfEdit === `CSS`) {
        setCss(code);
      } else if (tabAtTimeOfEdit === `JS`) {
        setJs(code);
      } else if (tabAtTimeOfEdit === `M`) {
        setEditManifest(code);
      }
    },
    1000,
  );

  // Wrapper that captures the current tab when the edit is initiated
  const handleCodeChange = (code: string) => {
    onCodeChanged(code, selectedTab.value);
  };

  return (
    <Wrapper>
      <FullscreenButton onClick={toggleFullscreen}>
        {isFullscreen.value ? (
          <AiOutlineFullscreenExit size={20} />
        ) : (
          <AiOutlineFullscreen size={20} />
        )}
      </FullscreenButton>
      <MonacoEditor
        code={currentCode.value}
        onCodeChange={handleCodeChange}
        lang={currentLang.value}
        readOnly={false}
        autoFormat
        monacoOptions={{ fontSize: 14 }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const FullscreenButton = styled.button`
  appearance: none;
  outline: none;
  border: none;
  background-color: white;
  box-shadow: 0 8px 10px 0 rgba(105, 122, 231, 0.11);
  height: 3.5rem;
  width: 3.5rem;
  border-radius: 100%;
  color: #969696;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;

  position: absolute;
  right: 1.5rem;
  top: 1rem;
  z-index: 1;

  &:hover {
    color: black;
  }

  &:active {
    color: #969696;
  }

  transition: color 0.2s ease-in-out;
`;
